import { apiClient } from '@/shared/lib/axios';
import type {
  AdminUserListItem,
  AdminUserMutationResult,
  AdminUserStorePayload,
  AdminUserUpdatePayload,
  AdminUsersListParams,
  AdminUsersListResponse,
} from './types';

type AdminUserMutationEnvelope = {
  success: boolean;
  data: AdminUserListItem;
  /** Present only when the save left an SSO link active; omitted entirely otherwise. */
  sso_password?: string;
};

/**
 * `sso_password` is a SIBLING of `data`, not a field inside the admin object.
 *
 * Both mutations used to return `response.data.data` and nothing else, so the password was
 * discarded one level above where anybody would think to look for it -- reading it off the admin
 * would just yield `undefined`, with no error to say why the operator never saw a password.
 */
const unwrapMutation = (body: AdminUserMutationEnvelope): AdminUserMutationResult => ({
  admin: body.data,
  ssoPassword: body.sso_password ?? null,
});

export const createAdminUser = async (
  site: string,
  payload: AdminUserStorePayload
): Promise<AdminUserMutationResult> => {
  const response = await apiClient.post<AdminUserMutationEnvelope>(
    `/admin/${site}/admins`,
    payload
  );
  return unwrapMutation(response.data);
};

export const getAdminUsers = async (
  site: string,
  params: AdminUsersListParams
): Promise<AdminUsersListResponse> => {
  const response = await apiClient.get<AdminUsersListResponse>(`/admin/${site}/admins`, { params });
  return response.data;
};

export const getAdminUser = async (
  site: string,
  id: number
): Promise<AdminUserListItem> => {
  const response = await apiClient.get<{ success: boolean; data: AdminUserListItem }>(
    `/admin/${site}/admins/${id}`
  );
  return response.data.data;
};

export const updateAdminUser = async (
  site: string,
  id: number,
  payload: AdminUserUpdatePayload
): Promise<AdminUserMutationResult> => {
  const response = await apiClient.put<AdminUserMutationEnvelope>(
    `/admin/${site}/admins/${id}`,
    payload
  );
  return unwrapMutation(response.data);
};

/**
 * Bấm lại nút 「SSO連携」 cho một 管理者 (BD sheet 2, mục 7.0).
 *
 * `password` chỉ cần khi tài khoản Cognito chưa từng được tạo — server trả 422 kèm
 * `errors.password` để màn hình biết mà hỏi. Trường hợp thường gặp (đẩy sang P2/P3 xong nhưng
 * ghi link hỏng) thì không cần mật khẩu, vì server tra ra tài khoản Cognito đang có.
 */
export const retryAdminSsoLink = async (
  site: string,
  id: number,
  password?: string
): Promise<AdminUserListItem> => {
  const response = await apiClient.post<{ success: boolean; data: AdminUserListItem }>(
    `/admin/${site}/admins/${id}/sso-link`,
    password ? { password } : {}
  );
  return response.data.data;
};

export const downloadAdminUsersCsv = async (
  site: string,
  params: AdminUsersListParams
): Promise<void> => {
  const response = await apiClient.get(`/admin/${site}/admins/csv`, {
    params,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=UTF-8;' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `admins${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
